/**
 * AssortmentCache — per-product IndexedDB cache.
 *
 * KEY DESIGN:
 *   Each product is stored individually under key "product:<numericProductId>".
 *   e.g. "product:6951260389505"
 *
 * RECORD SHAPE:
 *   { key: "product:123", html: "<div…>", storedAt: <timestamp> }
 *
 * EXPIRY STRATEGY — Schedule-based:
 *   Cache is globally cleared at configured wall-clock times in Europe/Berlin.
 *   Default schedule: 11:00 AM and 11:00 PM germany time.
 *
 *   Rule (simple and absolute):
 *     Any entry stored BEFORE the most recent flush(clear) point is expired.
 *
 *   Examples:
 *     • Entry stored at 11:01 AM → survives until 11:00 PM flush.
 *     • Entry stored at 10:59 AM → expired immediately after 11:00 AM flush.
 *     • Entry stored at 11:00:00.000 exactly → expired (flush point is inclusive).
 *
 *   Both morning and evening windows are independently enable/disable-able.
 *   If both are disabled, a 24 h fallback TTL is used.
 *
 * TIMEZONE:
 *   Europe/Berlin is the single source of truth (handles CET/CEST automatically).
 *   All users worldwide share the same flush boundary regardless of local time zone.
 *
 * Safe: all operations are try/caught — a cache failure never crashes the page.
 */

const AssortmentCache = (() => {

   // Schedule configuration
   /**
    * SCHEDULE defines the daily flush windows in Europe/Berlin local time.
    * Set `enabled: false` to disable a window independently.
    * `hour` and `minute` are 24-hour Berlin local time values.
    */
   const SCHEDULE = {
      morning: {
         enabled: true,
         hour: 10,
         minute: 0
      },
      evening: {
         enabled: true,
         hour: 23,
         minute: 0
      },
   };

   /** Fallback TTL used when ALL schedule windows are disabled. */
   const FALLBACK_TTL_MS = 24 * 60 * 60 * 1000; // 24h window

   /** IANA timezone — single source of truth for flush points. */
   const TIMEZONE = 'Europe/Berlin';

   //CONSTANTS
   const DB_NAME = 'assortment-cache';
   const DB_VERSION = 1;
   const STORE_NAME = 'products';

   let _db = null;
   let _openPromise = null;

   // ═══════════════════════════════════════════════════════════════════════════
   // SCHEDULE UTILITIES
   // ═══════════════════════════════════════════════════════════════════════════

   /**
    * Decompose a UTC timestamp into Europe/Berlin wall-clock parts.
    * @param  {number} timestampMs
    * @returns {{ year, month, day, hour, minute, second }}
    */
   function _toBerlinParts(timestampMs) {
      const fmt = new Intl.DateTimeFormat('en-GB', {
         timeZone: TIMEZONE,
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
         hour: '2-digit',
         minute: '2-digit',
         second: '2-digit',
         hour12: false,
      });

      const parts = fmt.formatToParts(new Date(timestampMs));
      const get = type => parseInt(parts.find(p => p.type === type)?.value ?? '0', 10);

      return {
         year: get('year'),
         month: get('month'),
         day: get('day'),
         hour: get('hour'),
         minute: get('minute'),
         second: get('second'),
      };
   }

   /**
    * Convert a Europe/Berlin wall-clock datetime to a UTC timestamp.
    * Probes both CET (+01:00) and CEST (+02:00) offsets and picks the one
    * that round-trips correctly — handles DST transitions transparently.
    *
    * @param  {number} year
    * @param  {number} month   1-based
    * @param  {number} day
    * @param  {number} hour 0-23 Berlin local
    * @param  {number} minute 0-59
    * @returns {number} UTC milliseconds
    */
   function _berlinToUTC(year, month, day, hour, minute) {
      const p = n => String(n).padStart(2, '0');

      for (const offsetHours of [2, 1]) {
         const sign = '+';
         const iso = `${year}-${p(month)}-${p(day)}T${p(hour)}:${p(minute)}:00${sign}0${offsetHours}:00`;
         const candidate = new Date(iso).getTime();
         const check = _toBerlinParts(candidate);

         if (check.year === year && check.month === month && check.day === day && check.hour === hour && check.minute === minute) {
            return candidate;
         }
      }

      // Absolute fallback (should never be reached)
      const p2 = n => String(n).padStart(2, '0');
      return new Date(`${year}-${p2(month)}-${p2(day)}T${p2(hour)}:${p2(minute)}:00+01:00`).getTime();
   }

   /**
    * Return all enabled flush-point UTC timestamps for a given Berlin calendar day,
    * sorted ascending.
    *
    * @param  {number} year
    * @param  {number} month  1-based
    * @param  {number} day
    * @returns {number[]}
    */
   function _flushPointsForDay(year, month, day) {
      const points = [];
      for (const window of Object.values(SCHEDULE)) {
         if (!window.enabled) continue;
         points.push(_berlinToUTC(year, month, day, window.hour, window.minute));
      }
      return points.sort((a, b) => a - b);
   }

   /**
    * Find the most recent flush point that occurred AT OR BEFORE `nowMs`.
    *
    * This is the single expiry boundary:
    *   storedAt <= lastFlushPoint  →  expired
    *   storedAt >  lastFlushPoint  →  fresh
    *
    * Returns null when no schedule windows are enabled (use FALLBACK_TTL_MS).
    *
    * @param  {number} [nowMs]
    * @returns {number|null}  UTC timestamp of the last flush point.
    */
   function _lastFlushPointBefore(nowMs = Date.now()) {
      const hasEnabled = Object.values(SCHEDULE).some(w => w.enabled);
      if (!hasEnabled) return null;

      const berlin = _toBerlinParts(nowMs);

      // Look back up to 2 days to guarantee we find the most recent point
      // even if today has no past flush points yet (e.g. it is 00:30 AM).
      const candidates = [];

      for (let back = 0; back <= 2; back++) {
         // Shift by whole days using UTC arithmetic then re-derive Berlin date
         const dayTs = _berlinToUTC(berlin.year, berlin.month, berlin.day, 0, 0) - back * 86_400_000;
         const dp = _toBerlinParts(dayTs);
         const pts = _flushPointsForDay(dp.year, dp.month, dp.day);

         for (const pt of pts) {
            if (pt <= nowMs) candidates.push(pt);
         }
      }

      return candidates.length ? Math.max(...candidates) : null;
   }

   /**
    * Return the UTC timestamp of the next flush point strictly AFTER `nowMs`.
    * Exposed for debugging / countdown timers.
    *
    * @param  {number} [nowMs]
    * @returns {number|null}
    */
   function _nextFlushPointAfter(nowMs = Date.now()) {
      const hasEnabled = Object.values(SCHEDULE).some(w => w.enabled);
      if (!hasEnabled) return null;

      const berlin = _toBerlinParts(nowMs);
      const candidates = [];

      for (let fwd = 0; fwd <= 1; fwd++) {
         const dayTs = _berlinToUTC(berlin.year, berlin.month, berlin.day, 0, 0) + fwd * 86_400_000;
         const dp = _toBerlinParts(dayTs);
         const pts = _flushPointsForDay(dp.year, dp.month, dp.day);

         for (const pt of pts) {
            if (pt > nowMs) candidates.push(pt);
         }
      }

      return candidates.length ? Math.min(...candidates) : null;
   }

   /**
    * Determine whether a single cache record is expired.
    * Centralised here so get() / getMany() stay consistent with evictExpired().
    *
    * @param  {number} storedAt
    * @param  {number} [nowMs]
    * @returns {boolean}
    */
   function _isExpired(storedAt, nowMs = Date.now()) {
      const lastFlush = _lastFlushPointBefore(nowMs);

      if (lastFlush === null) {
         // No schedule windows enabled — fallback TTL
         return (nowMs - storedAt) > FALLBACK_TTL_MS;
      }

      // Expired if stored AT or BEFORE the last flush point
      return storedAt <= lastFlush;
   }


   // ═══════════════════════════════════════════════════════════════════════════
   // DB LIFECYCLE
   // ═══════════════════════════════════════════════════════════════════════════

   function _open() {
      if (_db) return Promise.resolve(_db);
      if (_openPromise) return _openPromise;

      _openPromise = new Promise((resolve, reject) => {
         const req = indexedDB.open(DB_NAME, DB_VERSION);

         req.onupgradeneeded = e => {
            const db = e.target.result;
            const store = db.createObjectStore(STORE_NAME, {
               keyPath: 'key'
            });
            store.createIndex('storedAt', 'storedAt', {
               unique: false
            });
         };

         req.onsuccess = e => {
            _db = e.target.result;
            _db.onclose = () => {
               _db = null;
               _openPromise = null;
            };
            resolve(_db);
         };

         req.onerror = () => reject(req.error);
         req.onblocked = () => reject(new Error('[AssortmentCache] IDB open blocked'));
      });

      return _openPromise;
   }

   /** Wrap a single IDBRequest in a Promise. */
   async function _run(fn) {
      const db = await _open();
      return new Promise((resolve, reject) => {
         const r = fn(db);
         r.onsuccess = () => resolve(r.result);
         r.onerror = () => reject(r.error);
      });
   }

   /**
   * Execute a READ operation and resolve when the request succeeds.
   *
   * @param  {function(IDBDatabase): IDBRequest} fn
   * @returns {Promise<any>}
   */
   async function _runRead(fn) {
      const db = await _open();
      return new Promise((resolve, reject) => {
         const r = fn(db);
         r.onsuccess = () => resolve(r.result);
         r.onerror = () => reject(r.error);
      });
   }
 
  /**
   * Execute a WRITE operation and resolve only when the transaction commits.
   *
   * @param  {function(IDBDatabase, IDBTransaction): void} fn
   *         Receives the db AND the open transaction so the caller can queue
   *         multiple requests on the same transaction before it commits.
   * @returns {Promise<void>}
   */
   async function _runWrite(fn) {
      const db = await _open();
      return new Promise((resolve, reject) => {
         const tx = db.transaction(STORE_NAME, 'readwrite');
         tx.oncomplete = () => resolve();
         tx.onerror = () => reject(tx.error);
         tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'));
         fn(db, tx);
      });
   }

   /** Canonical cache key for a numeric product ID. */
   function _key(productId) {
      return `product:${productId}`;
   }

   // ═══════════════════════════════════════════════════════════════════════════
   // PUBLIC API
   // ═══════════════════════════════════════════════════════════════════════════

   /**
    * Read one product's cached HTML.
    * Returns null on miss or expiry.
    *
    * @param  {string|number} productId
    * @returns {Promise<string|null>}
    */
   async function get(productId) {
      try {
         const rec = await _runRead(db => db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(_key(productId)));
         if (!rec) return null;
         if (_isExpired(rec.storedAt)) {
            del(productId).catch(() => {});
            return null;
         }
         return rec.html;
      } catch (e) {
         return null;
      }
   }

   /**
    * Read multiple products' cached HTML in a single IDB transaction.
    * Returns Map<productId, html> — expired/missing entries are absent.
    *
    * @param  {string[]} productIds
    * @returns {Promise<Map<string, string>>}
    */
   async function getMany(productIds) {
      const result = new Map();
      if (!productIds.length) return result;

      try {
         const db = await _open();
         await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            let pending = productIds.length;
            const done = () => {
               if (--pending === 0) resolve();
            };

            for (const id of productIds) {
               const req = store.get(_key(id));
               req.onsuccess = () => {
                  const rec = req.result;
                  if (rec && !_isExpired(rec.storedAt)) {
                     result.set(id, rec.html);
                  } else if (rec) {
                     // expired — delete lazily outside this transaction
                     del(id).catch(() => {});
                  }
                  done();
               };
               req.onerror = () => done();
            }
         });
      } catch (e) {
         // Return whatever was collected before the error
      }
      return result;
   }

   /**
    * Write one product's HTML to cache.
    *
    * @param  {string|number} productId
    * @param  {string}        html
    */
   async function set(productId, html) {
      try {
         // await _runWrite(db => db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put({
         //    key: _key(productId),
         //    html,
         //    storedAt: Date.now()
         // }));
         await _runWrite((db, tx) => {
            tx.objectStore(STORE_NAME).put({ key: _key(productId), html, storedAt: now });
         });
      } catch (e) {
         console.warn('[AssortmentCache] set() failed:', e);
      }
   }

   /**
    * Write multiple products in a single IDB transaction.
    *
    * @param  {Array<{ productId: string, html: string }>} entries
    */
   async function setMany(entries) {
      if (!entries.length) return;
      try {
         const db = await _open();
         const now = Date.now();
         // await new Promise((resolve, reject) => {
         //    const tx = db.transaction(STORE_NAME, 'readwrite');
         //    const store = tx.objectStore(STORE_NAME);
         //    tx.oncomplete = resolve;
         //    tx.onerror = () => reject(tx.error);
         //    for (const { productId, html } of entries) {
         //       store.put({
         //          key: _key(productId),
         //          html,
         //          storedAt: now
         //       });
         //    }
         // });
         await _runWrite((db, tx) => {
            const store = tx.objectStore(STORE_NAME);
            for (const { productId, html } of entries) {
               store.put({ key: _key(productId), html, storedAt: now });
            }
         });
      } catch (e) {
         console.warn('[AssortmentCache] setMany() failed:', e);
      }
   }

   /**
    * Delete one product from cache.
    *
    * @param  {string|number} productId
    */
   async function del(productId) {
      console.log(`Remove from cached ::`,`productId >>`,productId)
      try {
         //await _runWrite(db => db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(_key(productId)));
         await _runWrite((db, tx) => { tx.objectStore(STORE_NAME).delete(_key(productId)) });
      } catch (e) {
         /* non-fatal */ 
      }
   }

   /**
    * Delete multiple products in a single IDB transaction.
    *
    * @param  {string[]|Set<string>} productIds
    */
   async function delMany(productIds) {
      const ids = [...productIds];
      if (!ids.length) return;
      try {
         const db = await _open();
         // await new Promise((resolve, reject) => {
         //    const tx = db.transaction(STORE_NAME, 'readwrite');
         //    const store = tx.objectStore(STORE_NAME);
         //    tx.oncomplete = resolve;
         //    tx.onerror = () => reject(tx.error);
         //    for (const id of ids) store.delete(_key(id));
         // });
         await _runWrite((db, tx) => {
            const store = tx.objectStore(STORE_NAME);
            for (const id of ids) store.delete(_key(id));
         });
      } catch (e) {
         console.warn('[AssortmentCache] delMany() failed:', e);
      }
   }

   /**
    * Return all stored records as { key, html, storedAt }[].
    *
    * @returns {Promise<object[]>}
    */
   async function getAll() {
      try {
         const db = await _open();
         return new Promise((resolve, reject) => {
            const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
            req.onsuccess = () => resolve(req.result ?? []);
            req.onerror = () => reject(req.error);
         });
      } catch (e) {
         return [];
      }
   }

   /**
    * Patch the quantity-input value for a specific variant inside a cached
    * product's HTML without resetting the entry's storedAt clock.
    *
    * @param  {string|number} productId
    * @param  {string|number} variantId
    * @param  {number}        qty        0 = remove from cart
    * @param  {string}        [lineKey]
    * @returns {Promise<boolean>}
    */
   async function patchCartQty(productId, variantId, qty, lineKey) {
    console.log(`patchCartQty ::`,`productId >>`,productId, `variantId >>`,variantId, `qty >>`,qty, `key >>`, lineKey)
      try {
         const rec = await _runRead(db => db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(_key(productId)));
         if (!rec?.html) return false;
         if (!rec.html.includes(`data-variant-id="${variantId}"`)) return false;

         const tmp = document.createElement('div');
         tmp.innerHTML = rec.html;

         const selector = `quantity-input[data-variant-id="${variantId}"] input`;
         const inputs = tmp.querySelectorAll(selector);
         if (!inputs.length) return false;

         const qtyStr = qty > 0 ? String(qty) : '';
         inputs.forEach(input => {
            input.value = qtyStr;
            input.setAttribute('value', qtyStr);
            const qInput = input.closest(`quantity-input[data-variant-id="${variantId}"]`);
            if (qInput) {
               qInput.classList.toggle('in-cart', qty > 0);
               if (qty === 0) {
                  qInput.removeAttribute('data-line-item');
               } else if (lineKey) {
                  qInput.setAttribute('data-line-item', lineKey);
               }
            }
         });

         // Preserve storedAt so the schedule boundary is not reset
         // await _runWrite(db => db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put({
         //    key: rec.key,
         //    html: tmp.innerHTML,
         //    storedAt: rec.storedAt
         // }));

         await _runWrite((db, tx) => {
            tx.objectStore(STORE_NAME).put({
               key: rec.key,
               html: tmp.innerHTML,
               storedAt: rec.storedAt
            });
         });

         return true;
      } catch (e) {
         console.warn('[AssortmentCache] patchCartQty() failed:', e);
         return false;
      }
   }

   /**
    * Evict all entries that fall before the most recent flush point.
    *
    * Implementation:
    *   Compute lastFlushPoint once → use IDBKeyRange.upperBound(lastFlushPoint)
    *   on the storedAt index → delete the entire range in one pass.
    *   This is O(expired) not O(total) and uses no JS-side per-record logic.
    *
    *   When no schedule is enabled, falls back to evicting entries older
    *   than FALLBACK_TTL_MS using the same range approach.
    *
    * @returns {Promise<number>}  Count of deleted entries.
    */
   async function evictExpired() {
      try {
         const db = await _open();
         const now = Date.now();

         // Determine the upper bound of storedAt values to delete
         const lastFlush = _lastFlushPointBefore(now);
         const boundary = lastFlush !== null ?
            lastFlush // schedule mode: everything ≤ lastFlush
            :
            now - FALLBACK_TTL_MS; // fallback mode: everything older than TTL

         if (boundary <= 0) return 0;

         return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            // upperBound(boundary, false) means storedAt <= boundary (inclusive)
            const range = IDBKeyRange.upperBound(boundary, false);
            const req = store.index('storedAt').openCursor(range);
            let n = 0;

            req.onsuccess = e => {
               const cursor = e.target.result;
               if (!cursor) {
                  resolve(n);
                  return;
               }
               cursor.delete();
               n++;
               cursor.continue();
            };

            req.onerror = () => reject(req.error);
         });
      } catch (e) {
         return 0;
      }
   }

   /** Wipe the entire store. */
   async function clear() {
      try {
         await _runWrite(db => db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).clear());
      } catch (e) {
         console.warn('[AssortmentCache] clear() failed:', e);
      }
   }

   // ═══════════════════════════════════════════════════════════════════════════
   // EXPORTS
   // ═══════════════════════════════════════════════════════════════════════════

   return {
      get,
      getMany,
      set,
      setMany,
      del,
      delMany,
      getAll,
      patchCartQty,
      // Maintenance
      evictExpired,
      clear,
      // Debugging / testing
      _isExpired,
      _lastFlushPointBefore,
      _nextFlushPointAfter,
      SCHEDULE,
   };

})();